import { AppButton, AppInput, AppTextarea, SelectionDropdown, Typography } from "@libs/zapi-ui/custom";
import { UploadSimpleIcon, InfoIcon, GlobeIcon } from "@phosphor-icons/react";
import { ACCEPTED_FILE_TYPES, languageOptions, MAX_FILES, MAX_TOTAL_SIZE_BYTES } from "../const/const";
import { MODEL_OPTIONS } from "@/zapi-frontend/shared/types/models";
import { ProjectErrors, TMinFileOrFile, TProjectDataWithoutRepository } from "../model/types";
import { useEffect, useRef, useState } from "react";
import { validateFilesLimit } from "@/zapi-frontend/shared/lib/validateFilesLimit";
import { toast } from "sonner";
import { useProjectTemplates } from "../model/useProjectTemplates";
import { RepositoryFile } from "./RepositoryFile";
import { RepositoryFileSkeleton } from "./RepositoryFileSkeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { AnalysisDropdown } from "./AnalysisDropdown";

export type TCreateProjectFormProps = {
  projectData: TProjectDataWithoutRepository;
  setProjectData: (projectData: TProjectDataWithoutRepository) => void;
  errors: ProjectErrors;
  setErrors: (errors: ProjectErrors) => void;
  files: TMinFileOrFile[];
  setFiles: (files: TMinFileOrFile[]) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isLabs?: boolean;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  skipPrefill?: boolean;
  onSkipPrefillChange?: (skip: boolean) => void;
  onFileAdd?: (files: TMinFileOrFile[]) => Promise<void>;
  onWebLinkAdd?: (url: string) => Promise<TMinFileOrFile | void>;
  isUploadingFiles?: boolean;
  uploadingFilesCount?: number;
};

export function CreateProjectForm(props: TCreateProjectFormProps) {
  const [stage, setStage] = useState(1);

  const {
    projectData,
    setProjectData,
    errors,
    setErrors,
    files,
    setFiles,
    onSubmit,
    onCancel,
    isLoading = false,
    isLabs = false,
    selectedModel,
    onModelChange,
    skipPrefill = false,
    onSkipPrefillChange,
    onFileAdd,
    onWebLinkAdd,
    isUploadingFiles = false,
    uploadingFilesCount = 0
  } = props;

  const { projectTemplates } = useProjectTemplates();

  const [webLinkDialogOpen, setWebLinkDialogOpen] = useState(false);
  const [webLinkUrl, setWebLinkUrl] = useState("");
  const [isAddingWebLink, setIsAddingWebLink] = useState(false);

  const updateProjectData = (updates: Partial<TProjectDataWithoutRepository>) => {
    setProjectData({ ...projectData, ...updates });
  };

  const updateErrors = (updates: Partial<ProjectErrors>) => {
    setErrors({ ...errors, ...updates });
  };

  const addFiles = (newFiles: TMinFileOrFile[]) => setFiles([...files, ...newFiles]);
  const removeFile = (fileToRemove: TMinFileOrFile) => setFiles(files.filter((f) => f !== fileToRemove));

  const isMaxFilesExceeded = files.length >= MAX_FILES;
  const isMaxTotalSizeExceeded = files.reduce((acc, file) => acc + file.size, 0) > MAX_TOTAL_SIZE_BYTES;

  const calculateTotalCharacterCount = (): number => {
    const description = projectData.description || "";
    const projectInstructions = projectData.projectInstructions || "";
    const decisionContext = projectData.decisionContext || "";
    const additionalContext = projectData.additionalContext || "";
    const targetAudience = projectData.targetAudience || "";
    const successMetrics = projectData.successMetrics || "";

    return (
      description.length +
      projectInstructions.length +
      decisionContext.length +
      additionalContext.length +
      targetAudience.length +
      successMetrics.length
    );
  };

  const totalCharCount = calculateTotalCharacterCount();
  const hasDocuments = files.length > 0;
  const shouldShowInsufficientInfoMessage = !hasDocuments && totalCharCount < 250;
  const shouldShowMinimalInfoMessage = !hasDocuments && totalCharCount >= 250 && totalCharCount < 750;

  const validateStage1 = () => {
    const newErrors: ProjectErrors = {};
    let isValid = true;

    if (!projectData.name.trim()) {
      newErrors.name = "Project name is required";
      isValid = false;
    }

    if (!projectData.language) {
      newErrors.language = "Language is required";
      isValid = false;
    }

    if (!isValid) {
      updateErrors(newErrors);
      toast.error("Please fill in all required fields");
    }

    return isValid;
  };

  const validateStage2 = () => {
    const newErrors: ProjectErrors = {};
    let isValid = true;

    if (!projectData.projectTemplateSlug) {
      newErrors.projectTemplateSlug = "Analysis type is required";
      isValid = false;
    }

    if (!projectData.decisionContext?.trim()) {
      newErrors.decisionContext = "Decision context is required";
      isValid = false;
    }

    if (!isValid) {
      updateErrors(newErrors);
      toast.error("Please fill in all required fields");
    }

    return isValid;
  };

  const attachmentFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const projectTemplateChapters =
      projectTemplates.find((s) => s.slug === projectData.projectTemplateSlug)?.projectTemplateChapters ?? null;
    if (projectTemplateChapters) {
      if (!projectData.chaptersInstructions || Object.keys(projectData.chaptersInstructions).length === 0) {
        const chaptersInstructions: Record<string, string> = {};
        projectTemplateChapters.forEach((chapter) => {
          chaptersInstructions[chapter.slug] = "";
        });
        updateProjectData({ chaptersInstructions });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData.projectTemplateSlug, projectTemplates]);

  const handleAddWebLink = async () => {
    if (!webLinkUrl.trim()) return;

    let url = webLinkUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `https://${url}`;
    }

    try {
      new URL(url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsAddingWebLink(true);
    try {
      if (onWebLinkAdd) {
        const minFile = await onWebLinkAdd(url);
        if (minFile) addFiles([minFile]);
      }
      setWebLinkDialogOpen(false);
      setWebLinkUrl("");
      toast.success("Web link added");
    } catch {
      toast.error("Failed to add web link");
    } finally {
      setIsAddingWebLink(false);
    }
  };

  const fileInput = (
    <input
      type="file"
      ref={attachmentFileInputRef}
      accept={ACCEPTED_FILE_TYPES}
      style={{ display: "none" }}
      multiple={true}
      disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading}
      onChange={async (e) => {
        if (e.target.files && e.target.files.length > 0) {
          const newFiles = Array.from(e.target.files);

          const errorMessage = validateFilesLimit({
            files: newFiles,
            maxFiles: MAX_FILES,
            maxTotalSizeBytes: MAX_TOTAL_SIZE_BYTES,
            existingFilesCount: files.length,
            existingTotalSizeBytes: files.reduce((acc, file) => acc + file.size, 0)
          });
          if (errorMessage) {
            toast.error(errorMessage);
            if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = "";
            return;
          }
          toast.dismiss();

          if (onFileAdd) {
            await onFileAdd(newFiles);
          } else {
            addFiles(newFiles);
          }

          if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = "";
        }
      }}
    />
  );

  if (stage === 1) {
    return (
      <div className="flex flex-col gap-8 w-full pt-8 pb-[80px]">
        <div className={cn("flex flex-col gap-6")}>
          <div className="flex flex-col items-start gap-4">
            <Typography variant="label4" color="zinc400">
              PROJECT SETUP 1/2
            </Typography>

            <Typography variant="heading5">Project Details</Typography>
          </div>
        </div>
        <div className="flex flex-col gap-y-4">
          <div className="flex gap-10">
            <div className="flex flex-col flex-grow gap-6">
              <AppInput
                label="Project Name*"
                value={projectData.name}
                onChange={(e) => {
                  updateProjectData({ name: e.target.value });
                  updateErrors({ name: "" });
                }}
                placeholder="e.g, Acme Project"
                inputError={errors.name}
                disabled={isLoading}
              />
            </div>
          </div>

          <AppTextarea
            label="Short Description"
            value={projectData.description ?? ""}
            fullWidth
            placeholder="e.g, To embed real-time, stage-aware, decision intelligence into every venture..."
            onChange={(e) => {
              updateProjectData({ description: e.target.value });
              updateErrors({ description: "" });
            }}
            textareaError={errors.description}
            isOptional
            className="min-h-[38px]"
            disabled={isLoading}
          />

          <SelectionDropdown
            label="Language Output*"
            options={languageOptions}
            placeholder="Select Language"
            value={projectData.language}
            onChange={(value) => {
              updateProjectData({ language: value });
              updateErrors({ language: "" });
            }}
            error={errors.language}
            disabled={isLoading}
          />
        </div>
        <Typography variant="heading6" className={cn("flex gap-2 items-center")}>
          Document Vault
          <Typography component="span" variant="body3" color="zinc400" className="font-light">
            Optional
          </Typography>
        </Typography>
        <div className="flex flex-col gap-6">
          <Typography variant="body2" color="zinc700" className="max-w-[600px]">
            Upload relevant files containing research, market analysis, business cases, or internal documentation.
            Supported formats: PDF, PowerPoint, Word, Excel, Images, Text, and CSV files. You can also add web links to
            scrape content from web pages.
          </Typography>
          <div className="flex flex-col px-4 py-5 gap-y-4 border border-innoveraZinc-150 rounded-[12px]">
            {(files.length > 0 || isUploadingFiles) && (
              <div className="flex flex-col items-start gap-3">
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AppButton
                        type="button"
                        disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading || isUploadingFiles}
                        appearance="outlined"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          attachmentFileInputRef.current?.click();
                        }}
                        startAdornment={<UploadSimpleIcon />}
                        color="zinc100"
                      >
                        Upload Files
                      </AppButton>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Typography variant="body2">
                        Upload up to {MAX_FILES} files (max {MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB total)
                      </Typography>
                    </TooltipContent>
                  </Tooltip>
                  {onWebLinkAdd && (
                    <AppButton
                      type="button"
                      disabled={isLoading || isUploadingFiles}
                      appearance="outlined"
                      onClick={() => setWebLinkDialogOpen(true)}
                      startAdornment={<GlobeIcon />}
                      color="zinc100"
                    >
                      Add Web Link
                    </AppButton>
                  )}
                </div>
              </div>
            )}

            {files.length > 0 || isUploadingFiles ? (
              <div className="flex flex-col gap-3">
                {files.map((file) => (
                  <RepositoryFile
                    key={file.name}
                    file={file}
                    onDelete={() => removeFile(file)}
                    disabled={isLoading || isUploadingFiles}
                  />
                ))}
                {isUploadingFiles &&
                  Array.from({ length: uploadingFilesCount }).map((_, index) => (
                    <RepositoryFileSkeleton key={`skeleton-${index}`} />
                  ))}
              </div>
            ) : (
              <div className="flex flex-col gap-5 items-center justify-center w-full max-w-[828px] h-[240px] text-center">
                <Typography variant="body3" color="zinc500">
                  Upload up to {MAX_FILES} files (max {MAX_TOTAL_SIZE_BYTES / 1024 / 1024}MB total)
                </Typography>
                <div className="flex gap-2">
                  <AppButton
                    type="button"
                    disabled={isMaxFilesExceeded || isMaxTotalSizeExceeded || isLoading || isUploadingFiles}
                    appearance="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      attachmentFileInputRef.current?.click();
                    }}
                    startAdornment={<UploadSimpleIcon />}
                    color="zinc100"
                  >
                    Upload Files
                  </AppButton>
                  {onWebLinkAdd && (
                    <AppButton
                      type="button"
                      disabled={isLoading || isUploadingFiles}
                      appearance="outlined"
                      onClick={() => setWebLinkDialogOpen(true)}
                      startAdornment={<GlobeIcon />}
                      color="zinc100"
                    >
                      Add Web Link
                    </AppButton>
                  )}
                </div>
              </div>
            )}
          </div>

          {fileInput}
        </div>
        <div className="flex gap-2 ml-auto">
          <AppButton
            type="button"
            appearance="outlined"
            className="w-fit ml-auto"
            color="zinc150"
            disabled={isLoading}
            onClick={onCancel}
          >
            Cancel
          </AppButton>
          <AppButton
            type="button"
            className="w-fit ml-auto"
            onClick={() => {
              if (validateStage1()) {
                setStage(2);
              }
            }}
            disabled={isLoading || isUploadingFiles}
          >
            Continue
          </AppButton>
        </div>

        <Dialog
          open={webLinkDialogOpen}
          onOpenChange={(open) => {
            if (!isAddingWebLink) {
              setWebLinkDialogOpen(open);
              if (!open) setWebLinkUrl("");
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Web Link</DialogTitle>
            </DialogHeader>
            <AppInput
              label="URL"
              placeholder="https://example.com"
              value={webLinkUrl}
              onChange={(e) => setWebLinkUrl(e.target.value)}
              disabled={isAddingWebLink}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddWebLink();
              }}
            />
            <DialogFooter>
              <AppButton
                appearance="outlined"
                color="zinc150"
                onClick={() => {
                  setWebLinkDialogOpen(false);
                  setWebLinkUrl("");
                }}
                disabled={isAddingWebLink}
              >
                Cancel
              </AppButton>
              <AppButton onClick={handleAddWebLink} isLoading={isAddingWebLink} disabled={!webLinkUrl.trim()}>
                Add
              </AppButton>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (stage === 2) {
    return (
      <div className="flex flex-col gap-8 w-full py-8 pb-[80px]">
        <div className={cn("flex flex-col gap-6")}>
          <div className="flex flex-col items-start gap-4">
            <Typography variant="label4" color="zinc400">
              PROJECT SETUP 2/2
            </Typography>

            <Typography variant="heading5">Decision Context</Typography>
          </div>
        </div>
        <div className="flex flex-col gap-y-4">
          <AnalysisDropdown
            value={projectData.projectTemplateSlug}
            onChange={(slug) => {
              updateProjectData({ projectTemplateSlug: slug });
              updateErrors({ projectTemplateSlug: "" });
            }}
            error={errors.projectTemplateSlug}
            projectInstructions={projectData.projectInstructions || ""}
            chaptersInstructions={projectData.chaptersInstructions || {}}
            onInstructionsChange={(projectInstructions, chaptersInstructions) => {
              updateProjectData({ projectInstructions, chaptersInstructions });
            }}
            disabled={isLoading}
          />

          <AppTextarea
            label="Question*"
            value={projectData.decisionContext ?? ""}
            fullWidth
            placeholder="e.g., Whether to productize a new invention"
            onChange={(e) => {
              updateProjectData({ decisionContext: e.target.value });
              updateErrors({ decisionContext: "" });
            }}
            textareaError={errors.decisionContext}
            className="min-h-[120px]"
            disabled={isLoading}
          />
          <Typography variant="body3" color="zinc500">
            Describe the core question or choice this analysis will resolve.
          </Typography>

          <AppTextarea
            label="How do you measure success?"
            value={projectData.successMetrics ?? ""}
            fullWidth
            placeholder="e.g., $50M operating profit by year 4, 25% faster project delivery, 10% increase in market share"
            onChange={(e) => {
              updateProjectData({ successMetrics: e.target.value });
              updateErrors({ successMetrics: "" });
            }}
            textareaError={errors.successMetrics}
            className="min-h-[120px]"
            isOptional
            disabled={isLoading}
          />
          <Typography variant="body3" color="zinc500">
            Describe what success looks like and how you'll measure it. You can include measurable targets (KPIs) or
            qualitative results (e.g., better collaboration, improved brand trust).
          </Typography>

          <AppTextarea
            label="Who will this analysis be presented to?"
            value={projectData.targetAudience ?? ""}
            fullWidth
            placeholder="e.g., Division GM, M&A Committee, CEO"
            onChange={(e) => {
              updateProjectData({ targetAudience: e.target.value });
              updateErrors({ targetAudience: "" });
            }}
            textareaError={errors.targetAudience}
            className="min-h-[38px]"
            isOptional
            disabled={isLoading}
          />
          <Typography variant="body3" color="zinc500">
            This helps tailor the tone, depth, and strategic focus of your report to the right audience level.
          </Typography>

          <AppTextarea
            label="Anything else we should know before starting the analysis?"
            value={projectData.additionalContext ?? ""}
            fullWidth
            onChange={(e) => {
              updateProjectData({ additionalContext: e.target.value });
              updateErrors({ additionalContext: "" });
            }}
            textareaError={errors.additionalContext}
            isOptional
            className="min-h-[120px]"
            disabled={isLoading}
          />
        </div>

        {(shouldShowInsufficientInfoMessage || shouldShowMinimalInfoMessage) && (
          <div className="flex items-start gap-3 p-4 bg-innoveraZinc-50 rounded-lg border border-innoveraZinc-150">
            <div className="flex-shrink-0 mt-0.5">
              <InfoIcon size={20} className="text-innoveraZinc-600" weight="regular" />
            </div>
            <Typography variant="body2" color="zinc700">
              {shouldShowInsufficientInfoMessage
                ? "You haven't yet supplied enough information to create a first draft. Please provide more details. Alternatively, you can proceed and start with an empty document."
                : "The information provided is minimal. The more information you provide, the better my analysis will be."}
            </Typography>
          </div>
        )}

        {isLabs && selectedModel && onModelChange && (
          <div className="flex flex-col gap-4 pb-2">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Typography variant="body3" className="tracking-wide">
                  Model
                </Typography>
                <Select value={selectedModel} onValueChange={onModelChange} disabled={skipPrefill || isLoading}>
                  <SelectTrigger
                    id="model-select"
                    className="h-8 text-sm min-w-[200px]"
                    data-testid="model-select-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {onSkipPrefillChange && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="skip-prefill"
                      checked={skipPrefill}
                      onCheckedChange={(checked) => onSkipPrefillChange(checked === true)}
                      disabled={isLoading}
                    />
                    <label
                      htmlFor="skip-prefill"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      <Typography variant="body3" color="zinc700">
                        Skip chapter generation
                      </Typography>
                    </label>
                  </div>
                )}
              </div>
              {onSkipPrefillChange && (
                <Typography variant="body3" color="zinc500" className="text-xs ml-0">
                  Note: Documents will still be parsed and business models generated even if chapter generation is
                  skipped.
                </Typography>
              )}
            </div>

            <AppTextarea
              label="Presentation Generation Instructions"
              value={projectData.presentationPrompt ?? ""}
              fullWidth
              placeholder={`Customize how presentation slides are generated. Leave empty for default behavior.

Example:
- Create 10-12 slides focused on executive summary
- Include financial projections and market analysis
- Use simple language suitable for board presentation`}
              onChange={(e) => {
                updateProjectData({ presentationPrompt: e.target.value });
              }}
              isOptional
              className="min-h-[120px]"
              disabled={isLoading}
            />
            <Typography variant="body3" color="zinc500">
              Custom instructions for AI when generating presentation slides. Describes slide structure, content focus,
              and formatting preferences. Leave empty to use default settings.
            </Typography>
          </div>
        )}

        <div className="flex gap-2 ml-auto">
          <AppButton
            appearance="outlined"
            className="w-fit ml-auto"
            color="zinc150"
            onClick={() => setStage(1)}
            disabled={isLoading}
          >
            Back
          </AppButton>
          <AppButton
            type="button"
            isLoading={isLoading}
            loadingText="Launching..."
            className="w-fit ml-auto"
            onClick={() => {
              if (validateStage2()) {
                onSubmit?.();
              }
            }}
          >
            {shouldShowInsufficientInfoMessage ? "Proceed with Empty Document" : "Launch Analysis"}
          </AppButton>
        </div>
      </div>
    );
  }

  return null;
}
